/* File: frontend/src/components/reputation/DetailedAuditReport.js */
/* Component: Ahrefs-style Audit Report with Action Plan */
/* Version: 1.2 (Platinum Master - Crash Safe) */
/* Date: Monday, December 8, 2025 at 4:30 PM EST */

import React from 'react';
import { 
    Activity, AlertTriangle, CheckCircle, ArrowRight, Layers, Zap 
} from 'lucide-react';
import { 
    PieChart, Pie, Cell, BarChart, Bar, Tooltip, ResponsiveContainer 
} from 'recharts';

// --- CONFIG ---
const COLORS = {
    green: '#10b981', // Emerald-500
    red: '#ef4444',   // Red-500
    orange: '#f59e0b',// Amber-500
    blue: '#3b82f6',  // Blue-500
    gray: '#e5e7eb'   // Gray-200
};

const DEFAULT_DATA = {
    score: 0,
    distribution: [],
    history: [],
    action_plan: []
};

const DetailedAuditReport = ({ reportData, onExecuteFix }) => {
    // 1. Safe Merge: Защита от null/undefined
    const safeData = reportData || DEFAULT_DATA;
    
    // 2. Safe Destructuring: Защита от отсутствующих полей внутри объекта
    const { 
        score = 0, 
        distribution = [], 
        history = [], 
        action_plan = [] 
    } = safeData;

    // Helper for Gauge Color
    const getScoreColor = (s) => {
        if (s >= 80) return COLORS.green;
        if (s >= 50) return COLORS.orange;
        return COLORS.red;
    };

    return (
        <div className="audit-report-container fade-in">
            
            {/* --- TOP BAR: HEALTH SCORE --- */}
            <div className="ahrefs-header-card">
                <div className="health-score-dial">
                    {/* CSS Conic Gradient for the gauge effect */}
                    <div className="score-circle" style={{
                        background: `conic-gradient(${getScoreColor(score)} ${score}%, ${COLORS.gray} 0)`
                    }}>
                        <div className="inner-circle">
                            <span className="score-number">{score}</span>
                            <span className="score-label">AI Health</span>
                        </div>
                    </div>
                </div>
                
                <div className="health-metrics">
                    <div className="metric-item">
                        <span className="metric-label">Total Issues</span>
                        <span className="metric-value" style={{color: COLORS.red}}>{action_plan.length}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">Critical</span>
                        <span className="metric-value" style={{color: COLORS.orange}}>
                            {action_plan.filter(i => i.severity === 'critical').length}
                        </span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">Status</span>
                        <span className="metric-value" style={{color: getScoreColor(score)}}>
                            {score >= 80 ? 'Excellent' : (score >= 50 ? 'At Risk' : 'Critical')}
                        </span>
                    </div>
                </div>

                <div className="history-chart-mini">
                    <div className="chart-label">Health Trend (30 Days)</div>
                    <ResponsiveContainer width="100%" height={60}>
                        <BarChart data={history.length > 0 ? history : [{date:'Now', score:score}]}>
                            <Bar dataKey="score" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={20} />
                            <Tooltip 
                                cursor={{fill: 'transparent'}} 
                                contentStyle={{fontSize:'12px', borderRadius:'8px', border:'none', boxShadow:'0 2px 5px rgba(0,0,0,0.1)'}} 
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* --- MAIN GRID --- */}
            <div className="audit-grid">
                
                {/* LEFT: ISSUES DISTRIBUTION (Donut) */}
                <div className="card report-card">
                    <div className="card-header-clean">
                        <h3><Activity size={18}/> Issue Distribution</h3>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie 
                                    data={distribution} 
                                    innerRadius={60} 
                                    outerRadius={80} 
                                    paddingAngle={5} 
                                    dataKey="value"
                                >
                                    {distribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="legend">
                            {distribution.map(d => (
                                <div key={d.name} className="legend-item">
                                    <span className="dot" style={{background: d.color}}></span>
                                    {d.name} ({d.value}%)
                                </div>
                            ))}
                            {distribution.length === 0 && <div className="text-muted text-center text-sm">No data available</div>}
                        </div>
                    </div>
                </div>

                {/* RIGHT: STRATEGIC ACTION PLAN */}
                <div className="card report-card plan-card">
                    <div className="card-header-clean">
                        <h3><Layers size={18}/> Strategic Action Plan</h3>
                        <button className="btn-xs btn-secondary">Download Report</button>
                    </div>
                    <div className="action-plan-list">
                        {action_plan.length === 0 && (
                            <div className="empty-plan">
                                <CheckCircle size={48} color={COLORS.green} style={{marginBottom:'10px'}}/>
                                <div>No issues detected. Your AI Reputation is pristine!</div>
                            </div>
                        )}
                        {action_plan.map(item => (
                            <div key={item.id} className={`plan-item ${item.severity}`}>
                                <div className="plan-icon">
                                    {item.status === 'completed' ? <CheckCircle size={20} className="text-green"/> : <AlertTriangle size={20}/>}
                                </div>
                                <div className="plan-content">
                                    <div className="plan-issue">
                                        <span className={`badge badge-${item.severity}`}>{item.severity}</span>
                                        {item.issue}
                                    </div>
                                    <div className="plan-strategy">
                                        <ArrowRight size={14} className="arrow-icon"/>
                                        <strong>Strategy:</strong> {item.strategy}
                                    </div>
                                    <div className="plan-meta">
                                        <span className="agent-badge">
                                            <Zap size={12}/> Agent: <strong>{item.agent}</strong>
                                        </span>
                                        {item.status !== 'completed' && onExecuteFix && (
                                            <button 
                                                className="btn-execute"
                                                onClick={() => onExecuteFix(item)}
                                            >
                                                Execute Fix
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailedAuditReport;